import { motion } from 'framer-motion';
import { ArrowLeft, Shield, Eye, Lock, FileText, Globe, Trash2, Database } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../contexts/I18nContext';
import BottomNav from '../components/BottomNav';

const PrivacyPage = () => {
  const navigate = useNavigate();
  const { t } = useI18n();

  const sections = [
    { icon: Database, color: 'bg-green-100 text-green-700', title: t('privacy.data_title'), content: t('privacy.data') },
    {
      icon: Eye, color: 'bg-purple-100 text-purple-700', title: t('privacy.use_title'),
      items: [t('privacy.use_1'), t('privacy.use_2'), t('privacy.use_3'), t('privacy.use_4'), t('privacy.use_5')]
    },
    { icon: Lock, color: 'bg-blue-100 text-blue-700', title: t('privacy.security_title'), content: t('privacy.security') },
    {
      icon: Shield, color: 'bg-emerald-100 text-emerald-700', title: t('privacy.gdpr_title'),
      items: [t('privacy.gdpr_1'), t('privacy.gdpr_2'), t('privacy.gdpr_3'), t('privacy.gdpr_4'), t('privacy.gdpr_5'), t('privacy.gdpr_6')]
    },
    { icon: Globe, color: 'bg-orange-100 text-orange-700', title: t('privacy.third_title'), content: t('privacy.third') },
    { icon: FileText, color: 'bg-cyan-100 text-cyan-700', title: t('privacy.cookies_title'), content: t('privacy.cookies') },
  ];

  return (
    <div className="w-full max-w-md mx-auto min-h-screen bg-[#F7F9FB] relative flex flex-col">
      <div className="p-6 pb-24 flex-1">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
          <div className="flex items-center gap-4 mb-6">
            <button onClick={() => navigate(-1)} data-testid="back-button"
              className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm active:scale-95 transition-transform">
              <ArrowLeft size={20} className="text-slate-900" />
            </button>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{t('privacy.title')}</h1>
          </div>

          <div className="bg-blue-50 p-4 rounded-xl mb-6">
            <p className="text-sm text-blue-900" style={{ fontFamily: 'Figtree, sans-serif' }}>{t('privacy.updated')}</p>
          </div>

          <div className="space-y-4">
            {sections.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} className="bg-white p-5 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.04)]">
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${s.color}`}><Icon size={20} /></div>
                    <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{s.title}</h2>
                  </div>
                  {s.content && <p className="text-sm text-slate-600 leading-relaxed" style={{ fontFamily: 'Figtree, sans-serif' }}>{s.content}</p>}
                  {s.items && (
                    <ul className="space-y-2 text-sm text-slate-600" style={{ fontFamily: 'Figtree, sans-serif' }}>
                      {s.items.map((item, j) => (
                        <li key={j} className="flex items-start gap-2">
                          <span className="text-emerald-600 mt-0.5 shrink-0">&#10003;</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}

            <div className="bg-white p-5 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.04)]">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-red-100 text-red-700 rounded-xl flex items-center justify-center"><Trash2 size={20} /></div>
                <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{t('privacy.deletion_title')}</h2>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed" style={{ fontFamily: 'Figtree, sans-serif' }}>{t('privacy.deletion')}</p>
            </div>

            <div className="bg-white p-5 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.04)]">
              <h2 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>{t('privacy.children_title')}</h2>
              <p className="text-sm text-slate-600 leading-relaxed" style={{ fontFamily: 'Figtree, sans-serif' }}>{t('privacy.children')}</p>
            </div>

            <div className="bg-white p-5 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.04)]">
              <h2 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>{t('privacy.updates_title')}</h2>
              <p className="text-sm text-slate-600 leading-relaxed" style={{ fontFamily: 'Figtree, sans-serif' }}>{t('privacy.updates')}</p>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-emerald-50 p-5 rounded-2xl border border-blue-100">
              <h2 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>{t('privacy.contact_title')}</h2>
              <p className="text-sm text-slate-600" style={{ fontFamily: 'Figtree, sans-serif' }}>{t('privacy.contact')}</p>
            </div>
          </div>
        </motion.div>
      </div>
      <BottomNav />
    </div>
  );
};

export default PrivacyPage;
